import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronRight, Monitor, Smartphone, AlertTriangle, Loader2 } from 'lucide-react';

// Custom icons for products
const DrillIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M14 2l4 4-8 8-4-4 8-8z" />
    <path d="M6 14l-4 4 4 4 4-4" />
    <path d="M10 10l-6 6" />
  </svg>
);

const ShovelIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2 22l6-6" />
    <path d="M8 16l-4 4" />
    <path d="M10 14l8-8 4 4-8 8z" />
    <path d="M18 6l2-2" />
  </svg>
);

const TrenchIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4 12h16" />
    <path d="M4 6h16" />
    <path d="M4 18h16" />
    <rect x="2" y="4" width="4" height="16" rx="1" />
    <rect x="18" y="4" width="4" height="16" rx="1" />
  </svg>
);

// Coming Soon Component
const ComingSoon = ({ message }) => (
  <div className="min-h-screen bg-[#0D0D1A] flex items-center justify-center p-6">
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#0073E6]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#00E600]/10 rounded-full blur-3xl" />
    </div>
    
    <div className="relative text-center max-w-lg">
      <img 
        src="/api/utilityops/logo.png" 
        alt="Utility Ops" 
        className="h-16 mx-auto mb-8"
      />
      <div className="w-20 h-20 rounded-full bg-[#FFDA00]/10 flex items-center justify-center mx-auto mb-6">
        <AlertTriangle className="h-10 w-10 text-[#FFDA00]" />
      </div>
      <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
        Coming Soon
      </h1>
      <p className="text-gray-400 text-lg mb-8">
        {message}
      </p>
      <a 
        href="/api/admin-portal"
        className="inline-flex items-center gap-2 px-6 py-3 border border-[#2A2A4A] text-white font-medium rounded-lg hover:bg-[#1A1A33] transition-colors"
      >
        Go to Admin Portal
      </a>
    </div>
  </div>
);

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check site settings on load
  useEffect(() => {
    const checkSiteSettings = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${API_URL}/api/saas/site-settings`);
        if (response.ok) {
          const data = await response.json();
          setSiteSettings(data);
        }
      } catch (error) {
        console.error('Failed to load site settings:', error);
      } finally {
        setLoading(false);
      }
    };
    checkSiteSettings();
  }, []);

  // Show loading spinner
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D1A] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#0073E6]" />
      </div>
    );
  }

  // Show Coming Soon if maintenance mode is enabled
  if (siteSettings?.maintenance_mode) {
    return <ComingSoon message={siteSettings.coming_soon_message} />;
  }

  const tools = [
    {
      id: 'bore-boss',
      name: 'Bore Boss',
      subtitle: 'Directional Drilling',
      description: 'Track every rod, log every depth, document every utility crossing. Built for HDD crews who need accurate records without the paperwork.',
      icon: DrillIcon,
      color: '#FFDA00',
      available: true,
      capabilities: [
        'Rod-by-rod depth and pitch tracking',
        'GPS coordinates at entry/exit points',
        'Utility crossing documentation with photos',
        'Real-time bore profile visualization',
        'PDF reports for clients and inspectors'
      ]
    },
    {
      id: 'plow-boss',
      name: 'Plow Boss',
      subtitle: 'Vibratory Plow',
      description: 'Continuous depth logging for direct burial installations. Know exactly where your cable is, from start to finish.',
      icon: ShovelIcon,
      color: '#0073E6',
      available: false,
      capabilities: [
        'Continuous depth recording',
        'GPS route mapping',
        'Material and footage tracking',
        'Utility locate documentation',
        'As-built report generation'
      ]
    },
    {
      id: 'trench-boss',
      name: 'Trench Boss',
      subtitle: 'Open Cut Trenching',
      description: 'Document trench dimensions, bedding, backfill, and compaction. Everything inspectors need, nothing they don\'t.',
      icon: TrenchIcon,
      color: '#00E600',
      available: false,
      capabilities: [
        'Trench depth and width logging',
        'Bedding and backfill records',
        'Compaction test documentation',
        'Photo documentation at each stage',
        'Inspector-ready reports'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D1A]">
      {/* Navigation - Simple and clean */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0D0D1A]/95 backdrop-blur border-b border-[#1A1A33]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <img src="/api/utilityops/logo.png" alt="Utility Ops" className="h-10" />
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#tools" className="text-gray-400 hover:text-white text-sm">Tools</a>
              <a href="#how-it-works" className="text-gray-400 hover:text-white text-sm">How It Works</a>
              <a href="/api/admin-portal" className="text-white text-sm font-medium">Sign In</a>
            </div>

            <button 
              className="md:hidden text-gray-400"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-[#1A1A33] border-t border-[#2A2A4A] px-6 py-4 space-y-4">
            <a href="#tools" className="block text-gray-400 hover:text-white">Tools</a>
            <a href="#how-it-works" className="block text-gray-400 hover:text-white">How It Works</a>
            <a href="/api/admin-portal" className="block text-white font-medium">Sign In</a>
          </div>
        )}
      </nav>

      {/* Hero - Direct and honest */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#0073E6] text-sm font-medium tracking-wide uppercase mb-4">
            Field Documentation Software
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
            Underground utility installation<br />
            <span className="text-gray-400">documented right.</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mb-8">
            Track depths, log coordinates, photograph utilities, generate reports. 
            Works offline in the field, syncs when you're back in range.
          </p>
          <div className="flex flex-wrap gap-4">
            <a 
              href="#tools"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0D0D1A] font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              See the tools
              <ChevronRight className="h-4 w-4" />
            </a>
            <a 
              href="/api/admin-portal"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#2A2A4A] text-white font-medium rounded-lg hover:bg-[#1A1A33] transition-colors"
            >
              Sign in
            </a>
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section id="tools" className="py-20 px-6 bg-[#0a0a14]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">The Tools</h2>
            <p className="text-gray-400 max-w-xl">
              Each tool is purpose-built for a specific installation method. 
              Use what you need.
            </p>
          </div>

          <div className="space-y-8">
            {tools.map((tool) => (
              <div 
                key={tool.id}
                className="bg-[#1A1A33] rounded-lg border border-[#2A2A4A] overflow-hidden"
              >
                <div className="p-8 md:p-10">
                  <div className="flex flex-col md:flex-row md:items-start gap-8">
                    {/* Left side - Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${tool.color}15` }}
                        >
                          <tool.icon className="h-6 w-6" style={{ color: tool.color }} />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">{tool.name}</h3>
                          <p className="text-sm text-gray-500">{tool.subtitle}</p>
                        </div>
                        {!tool.available && (
                          <span className="ml-auto text-xs text-gray-500 bg-[#2A2A4A] px-3 py-1 rounded">
                            In Development
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 mb-6 leading-relaxed">
                        {tool.description}
                      </p>
                      {tool.available && (
                        <a 
                          href="/api/admin-portal"
                          className="inline-flex items-center gap-2 text-sm font-medium hover:gap-3 transition-all"
                          style={{ color: tool.color }}
                        >
                          Open {tool.name}
                          <ChevronRight className="h-4 w-4" />
                        </a>
                      )}
                    </div>

                    {/* Right side - Capabilities */}
                    <div className="md:w-80">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">What it does</p>
                      <ul className="space-y-2">
                        {tool.capabilities.map((cap, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
                            <span 
                              className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                              style={{ backgroundColor: tool.color }}
                            />
                            {cap}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Simple explanation */}
      <section id="how-it-works" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">How It Works</h2>
            <p className="text-gray-400 max-w-xl">
              Your crew uses the mobile app in the field. You manage everything from the web.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Field App */}
            <div className="bg-[#1A1A33] rounded-lg border border-[#2A2A4A] p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-[#0073E6]/10 flex items-center justify-center">
                  <Smartphone className="h-6 w-6 text-[#0073E6]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Field App</h3>
                  <p className="text-sm text-gray-500">iOS & Android</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li>• Works offline - syncs when connected</li>
                <li>• Log depths, coordinates, photos</li>
                <li>• Voice notes for quick documentation</li>
                <li>• See bore profile in real-time</li>
                <li>• Generate PDF on-site if needed</li>
              </ul>
            </div>

            {/* Admin Portal */}
            <div className="bg-[#1A1A33] rounded-lg border border-[#2A2A4A] p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-[#00E600]/10 flex items-center justify-center">
                  <Monitor className="h-6 w-6 text-[#00E600]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Admin Portal</h3>
                  <p className="text-sm text-gray-500">Web-based</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li>• View all projects and crew activity</li>
                <li>• Generate detailed PDF reports</li>
                <li>• Map view of all installations</li>
                <li>• Manage users and permissions</li>
                <li>• Export data for clients</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Simple CTA */}
      <section className="py-20 px-6 bg-[#0a0a14]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Ready to use it?
          </h2>
          <p className="text-gray-400 mb-8">
            Sign in to access your tools, or contact us if you're new.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="/api/admin-portal"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0D0D1A] font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              Sign In
            </a>
            <a 
              href="mailto:contact@utilityops.com"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#2A2A4A] text-white font-medium rounded-lg hover:bg-[#1A1A33] transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer - Minimal */}
      <footer className="py-8 px-6 border-t border-[#1A1A33]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/api/utilityops/logo.png" alt="Utility Ops" className="h-8" />
          <p className="text-gray-500 text-sm">
            © 2025 Utility Ops
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
